import { AGENTS_DEFAULTS, type AgentsConfig } from '@inboundx/shared/types';
import { supabase } from '@/lib/supabase';
import { upsertClientConfig } from '@/services/config';

export interface AgentDefinition {
    id: string;
    name: string;
    status: 'active' | 'beta' | 'preview' | 'coming_soon';
    enabled: boolean;
    description?: string;
}

interface WorkspaceAgentOverrides {
    agents: Record<string, { enabled?: boolean }>;
}

/**
 * Get global agent definitions from schema defaults (synchronous).
 * The agent catalog (names, descriptions, statuses) is baked into the schema.
 */
export function getGlobalAgents(): AgentDefinition[] {
    const agents = AGENTS_DEFAULTS.agents ?? {};
    return Object.entries(agents).map(([id, def]) => ({
        id,
        name: def.name,
        status: def.status,
        enabled: def.enabled,
        description: def.description,
    }));
}

/**
 * Fetch workspace-specific agent overrides from config.client_configs.
 */
export async function fetchWorkspaceAgentOverrides(
    domain: string
): Promise<WorkspaceAgentOverrides | null> {
    const { data, error } = await supabase
        .schema('config')
        .from('client_configs')
        .select('config')
        .eq('domain', domain)
        .eq('config_slug', 'agents')
        .eq('config_scope', 'global')
        .single();

    if (error) {
        if (error.code === 'PGRST116') return null;
        throw error;
    }

    const config = data?.config as AgentsConfig | null;
    return config?.agents
        ? { agents: config.agents as Record<string, { enabled?: boolean }> }
        : null;
}

/**
 * The overrides a workspace under a path resolves: its host's, with its own laid over
 * per agent. Same rule as the runtime (`config.workspace_config_rows` deep-merges
 * `agents`), so the toggles show what actually runs when the host changes later.
 */
export function mergeWorkspaceAgentOverrides(
    host: WorkspaceAgentOverrides | null,
    own: WorkspaceAgentOverrides | null
): WorkspaceAgentOverrides | null {
    if (!host || !own) return own ?? host;
    const agents = { ...host.agents };
    for (const [id, override] of Object.entries(own.agents)) {
        agents[id] = { ...agents[id], ...override };
    }
    return { agents };
}

/**
 * Merge global agents with workspace-specific overrides.
 */
export function mergeAgentConfigs(
    globalAgents: AgentDefinition[],
    workspaceOverrides: WorkspaceAgentOverrides | null
): AgentDefinition[] {
    const overrides = workspaceOverrides?.agents ?? {};

    return globalAgents.map((agent) => ({
        ...agent,
        enabled: overrides[agent.id]?.enabled ?? agent.enabled,
    }));
}

/**
 * Toggle an agent's enabled state for a workspace identified by its config key.
 * Writes to config.client_configs via upsert.
 *
 * `currentOverrides` must be the workspace's OWN row, never the resolved host+own
 * merge — otherwise a path workspace's first toggle would freeze every inherited
 * agent as an explicit own-value and stop it inheriting later host changes. Own
 * entries are preserved verbatim (no undefined→false coercion), so a workspace
 * stores only the agents it has actually overridden.
 */
export async function updateWorkspaceAgentEnabled(
    domain: string,
    agentId: string,
    enabled: boolean,
    currentOverrides: Record<string, { enabled?: boolean }>
): Promise<void> {
    const newAgents: Record<string, { enabled?: boolean }> = {
        ...currentOverrides,
        [agentId]: { enabled },
    };

    await upsertClientConfig(domain, 'agents', 'global', {
        config: { agents: newAgents },
    });
}
